import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Input } from '@app/components/common/inputs/Input/Input';
import { Modal } from '@app/components/common/Modal/Modal';
import { UploadFile, UploadProps } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFileProps, handlePreview } from '@app/utils/file';
import { getFileWithSignedUrl, uploadFile } from '@app/api/file.api';
import { getReadableFileName, normFile } from '@app/utils/utils';
import mime from 'mime-types';
import { UpdateImgResponse } from '@app/interfaces/interfaces';
import { Entity } from '@app/constants/enums/entites';
import { RcFile } from 'antd/lib/upload';
import { notificationController } from '@app/controllers/notificationController';
import axios from 'axios';
import { IFile } from '@app/interfaces/file';
import * as S from '@app/components/common/Modal/DealRoomUploadFileModal/DealRoomUploadFileModal.styles';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UploadOutlined } from '@ant-design/icons';
import { IDealRoomMenu } from '@app/interfaces/deal-room-menu';
import { createDealRoomMenu, updateDealRoomMenu } from '@app/api/deal-room-menu.api';

interface AddDealRoomMenuProps {
  open: boolean;
  dealRoomId: string;
  dealRoomMenu?: IDealRoomMenu | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const AddDealRoomMenu: React.FC<AddDealRoomMenuProps> = ({
  open,
  dealRoomId,
  dealRoomMenu,
  onSuccess,
  onCancel,
}) => {
  const [form] = BaseForm.useForm();
  const { t } = useTranslation();

  const [loader, setLoader] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const fileProps = getFileProps({
    maxCount: 1,
    multiple: false,
    accept: 'application/pdf',
    fileList,
    setList: setFileList,
    handlePreview,
  });

  async function fetchImage(image: string) {
    try {
      const file = await getFileWithSignedUrl(image);
      return file;
    } catch (error) {}
  }

  const fetchImages = useCallback(
    async (field: string, images: string[], setList: React.Dispatch<React.SetStateAction<UploadFile[]>>) => {
      const files = [];
      for (const file of images) {
        try {
          const i = await fetchImage(file);
          if (i)
            files.push({
              ...i,
              url: i.signedUrl,
            });
        } catch (error) {}
      }

      const mappedFiles = files.map((file) => {
        return {
          uid: file.id,
          id: file.id,
          name: getReadableFileName(file.originalName as string),
          url: file.originalName,
          type: file.originalName ? mime.lookup(file.originalName) : '',
          showPreviewIcon: false,
        };
      });

      form.setFieldsValue({ [field]: mappedFiles });
      setList(mappedFiles as UploadFile[]);
    },
    [form],
  );

  async function syncUploadFile(id: string, file: UploadProps<unknown>): Promise<UpdateImgResponse> {
    return await uploadFile({
      entityId: id,
      entity: Entity.DEAL_ROOM_MENU,
      file: (file as UploadFile).originFileObj as RcFile,
    });
  }

  const onSubmit = async (values: Partial<IDealRoomMenu>) => {
    setLoader(true);
    try {
      const dealRoomMenuId = dealRoomMenu?.id || null;

      // Ensure file is always an array
      const files = Array.isArray(values.file) ? values.file : values.file ? [values.file] : [];

      const pFiles = files.filter((item: UploadFile) => !item.url);

      const formFiles = Array.isArray(form.getFieldValue('file'))
        ? form.getFieldValue('file')
        : form.getFieldValue('file')
        ? [form.getFieldValue('file')]
        : [];

      const allFiles = formFiles.filter((item: UploadFile) => item.url).map((item: IFile) => item.id);

      let file = allFiles.length > 0 ? allFiles[0] : null;
      if (pFiles.length > 0) {
        const newFiles = await syncUploadFile(dealRoomId || '', pFiles[0]);
        file = newFiles.id;
      }

      const payload = {
        ...values,
        file,
        dealRoom: dealRoomId,
      };

      dealRoomMenuId ? await updateDealRoomMenu(dealRoomMenuId as string, payload) : await createDealRoomMenu(payload);
      notificationController.success({
        message: t(
          dealRoomMenuId ? 'dealRooms.selectMenu.updatedSuccessfully' : 'dealRooms.selectMenu.createdSuccessfully',
        ),
      });
      onSuccess();
    } catch (error) {
      if (!axios.isCancel(error)) notificationController.error({ message: (error as Error).message });
    } finally {
      setLoader(false);
    }
  };

  const validateLink = async (_: unknown, value: string | null | undefined) => {
    if (!value) {
      return Promise.resolve();
    }

    try {
      // Simple format check
      if (!/^https?:\/\/.+/.test(value)) {
        return Promise.reject(new Error(t('dealRooms.selectMenu.signatureLinkMustBeValid')));
      }

      return Promise.resolve();
    } catch (err) {
      return Promise.reject(new Error(t('dealRooms.selectMenu.signatureLinkMustBeValid')));
    }
  };

  const onOk = () => {
    form.submit();
  };

  useEffect(() => {
    if (dealRoomMenu) {
      if ((dealRoomMenu?.file as IFile)?.id) {
        fetchImages('file', [(dealRoomMenu?.file as IFile)?.id], setFileList);
      }
      form.setFieldsValue({
        name: dealRoomMenu.name,
        link: dealRoomMenu.link,
      });
    }
  }, [form, fetchImages, dealRoomMenu]);

  return (
    <Modal
      size="large"
      title={t(
        dealRoomMenu?.id ? 'dealRooms.selectMenu.updateDealRoomSection' : 'dealRooms.selectMenu.createDealRoomSection',
      )}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      okText={t(dealRoomMenu?.id ? 'dealRooms.selectMenu.update' : 'dealRooms.selectMenu.create')}
      cancelText={t('common.cancel')}
      confirmLoading={loader}
    >
      <BaseForm form={form} layout="vertical" name="dealRoomMenuForm" onFinish={onSubmit} style={{ width: '100%' }}>
        <BaseForm.Item
          name="name"
          rules={[{ required: true, message: t('dealRooms.selectMenu.nameRequired') }]}
          label={t('dealRooms.selectMenu.name')}
        >
          <Input placeholder={t('dealRooms.selectMenu.name')} />
        </BaseForm.Item>
        <BaseForm.Item
          name="file"
          label={t('dealRooms.selectMenu.file')}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value && value.length > 0) {
                  return Promise.resolve();
                }
                if (getFieldValue('link')) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('dealRooms.selectMenu.fileOrSignatureLinkRequired')));
              },
            }),
          ]}
        >
          <BaseForm.Item name="file" valuePropName="setFileList" getValueFromEvent={normFile}>
            <S.UploadWrapper name="file" {...fileProps}>
              <Button type="default" icon={<UploadOutlined />}>
                {t('dealRooms.selectMenu.clickToUpload')}
              </Button>
            </S.UploadWrapper>
          </BaseForm.Item>
        </BaseForm.Item>
        <BaseForm.Item
          name="link"
          label={t('dealRooms.selectMenu.signatureLink')}
          rules={[
            { validator: validateLink },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                if (getFieldValue('file') && getFieldValue('file').length > 0) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('dealRooms.selectMenu.fileOrSignatureLinkRequired')));
              },
            }),
          ]}
        >
          <Input placeholder={t('dealRooms.selectMenu.signatureLink')} />
        </BaseForm.Item>
      </BaseForm>
    </Modal>
  );
};
